import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import SideNavigation from '@amzn/awsui-components-console/side-navigation';
import Spinner from '@amzn/awsui-components-console/spinner';
import Box from '@amzn/awsui-components-console/box';
import {
	getBindleLockAuthorizationRequest,
	getAllBindleLocks,
	getBindleLockForNodeType,
} from 'src/authentication/bindle-utils';
import { logger } from 'src/utils/logger';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { RAD_NODE_TYPE } from '../../RADWorkflow/RADWorkflowConstants';
import { v4 as uuidv4 } from 'uuid';

import { AuthContext } from '../../../providers/AuthContext';
import { ANCHOR_POINT_NODE_TYPE } from 'src/pages/AnchorPointWorkflow/AnchorPointNodeConstants';
import RecentlyVisitedList, { addRecentlyVisited } from '../recentlyVisited/RecentlyVisited';

interface NavigationLink {
	type: 'link';
	text: string;
	href: string;
	info?: React.ReactNode;
}

interface NavigationSection {
	type: 'section';
	text: string;
	items: NavigationLink[];
}

interface NavigationExpandableLinkGroup {
	type: 'expandable-link-group';
	text: string;
	href: string;
	items: NavigationLink[];
}

type NavigationItem = NavigationLink | NavigationSection | NavigationExpandableLinkGroup;

interface SidePanelProps extends RouteComponentProps {}

interface SidePanelState {
	authorizedLocks: string[];
	validatedAuth: boolean;
	cachedItems: NavigationItem[] | null;
}

// Class SidePanel is the Side Navigation component that is used in BasicLayout, CreateForm, ServiceHomepage, and Table flows.
// Implement like this: <SidePanel />
class SidePanel extends React.Component<SidePanelProps, SidePanelState> {
	static contextType = AuthContext;

	context!: React.ContextType<typeof AuthContext>;

	constructor(props: SidePanelProps) {
		super(props);
		this.state = {
			authorizedLocks: [],
			validatedAuth: false,
			cachedItems: null,
		};
		this.getAuthorizedLocks = this.getAuthorizedLocks.bind(this);
	}

	componentDidMount(): void {
		this.getAuthorizedLocks();
		const { pathname, search } = this.props.location;

		if (pathname && pathname !== '/') {
			const fullPath = search ? `${pathname}${search}` : pathname;

			addRecentlyVisited(fullPath);
		}
	}

	componentDidUpdate(prevProps: SidePanelProps): void {
		const { pathname, search } = this.props.location;
		const prevPathname = prevProps.location.pathname;
		const prevSearch = prevProps.location.search;
		
		if (pathname !== prevPathname || search !== prevSearch) {
			if (pathname && pathname !== '/') {
				const fullPath = search ? `${pathname}${search}` : pathname;

				addRecentlyVisited(fullPath);
			}
		}
	}

	buildNavigationItems(): void {
		
		const items = this.getNavigationItems();
		
		this.setState({ cachedItems: items });
	}

	getNavigationItems(): NavigationItem[] {

		const entitySearch: NavigationLink = {
			type: 'link',
			text: 'Entity Search',
			href: '#/entitySearch',
		};
		const nodeNameValidator: NavigationLink = {
			type: 'link',
			text: 'Node Name Validator',
			href: '#/nodeNameValidator',
		};
		const executeWorkflows: NavigationLink = {
			type: 'link',
			text: 'Execute Workflows',
			href: '#/executeWorkflows',
		};
		const manageWorkflows: NavigationLink = {
			type: 'link',
			text: 'Manage Workflows',
			href: '#/manageWorkflows',
		};
		const trackExecutions: NavigationLink = {
			type: 'link',
			text: 'Track Executions',
			href: '#/manageExecution',
		};
		const viewNotifications: NavigationLink = {
			type: 'link',
			text: 'View Notifications',
			href: '#/viewNotifications',
		};
		const notificationManager: NavigationLink = {
			type: 'link',
			text: 'Manage Notification',
			href: '#/notificationManager',
		};

		if (!this.state.validatedAuth) {
			return [
				entitySearch,
				executeWorkflows,
				manageWorkflows,
				trackExecutions,
				nodeNameValidator,
				notificationManager,
				viewNotifications,
				{ type: 'link', text: 'Loading', href: '/', info: <Spinner></Spinner> } as NavigationLink,
			];
		}

		const authorisedSidePanelLinks: NavigationItem[] = [
			entitySearch,
			executeWorkflows,
			manageWorkflows,
			trackExecutions,
		];

		if (
			this.state.authorizedLocks.includes(
				getBindleLockForNodeType('application_management')
			)
		) {
			authorisedSidePanelLinks.push({
				type: 'link',
				text: 'Application Management',
				href: '#/applicationManagement',
			});
		}

		if (
			this.state.authorizedLocks.includes(
				getBindleLockForNodeType('attribute_management')
			)
		) {
			authorisedSidePanelLinks.push({
				type: 'link',
				text: 'Attribute Management',
				href: '#/attributeManagement',
			});
		}

		authorisedSidePanelLinks.push(nodeNameValidator,notificationManager,viewNotifications);

		authorisedSidePanelLinks.push({
			type: 'link',
			text: 'App Config Dashboard',
			href: '#/appConfig',
		});

		const nodeWorkflowItems: NavigationLink[] = [];

		if (
			this.state.authorizedLocks.includes(getBindleLockForNodeType('p2r_fc'))
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'P2R Workflow',
				href: '#/launchP2RWorkflow',
			});
		}

		if (
			this.state.authorizedLocks.includes(
				getBindleLockForNodeType(RAD_NODE_TYPE)
			)
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'RAD Workflow',
				href: '#/launchRadWorkflow',
			});
		}

		if (
			this.state.authorizedLocks.includes(getBindleLockForNodeType('reconfig'))
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'FC Reconfiguration Workflow',
				href: '#/launchFCReconfig',
			});
		}

		if (
			this.state.authorizedLocks.includes(getBindleLockForNodeType('sorter'))
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'Sorter Setup Workflow',
				href: '#/launchSorterWorkflow',
			});
		}

		if (
			this.state.authorizedLocks.includes(
				getBindleLockForNodeType('dockmaster')
			)
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'Dockmaster Workflow',
				href: '#/launchDockmasterWorkflow',
			});
		}

		if (
			this.state.authorizedLocks.includes(
				getBindleLockForNodeType('node_address_update')
			)
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'FC Address Update Workflow',
				href: '#/launchNodeAddressUpdateWorkflow',
			});
		}

		if (
			this.state.authorizedLocks.includes(
				getBindleLockForNodeType(ANCHOR_POINT_NODE_TYPE)
			)
		) {
			nodeWorkflowItems.push({
				type: 'link',
				text: 'Anchor Point Workflow',
				href: '#/launchAnchorPointWorkflow',
			});
		}

		if (nodeWorkflowItems.length > 0) {
			authorisedSidePanelLinks.push({
				type: 'expandable-link-group',
				text: 'Node Workflows',
				href: '#',
				items: nodeWorkflowItems,
			} as NavigationExpandableLinkGroup);
		}

		return authorisedSidePanelLinks;
	}

	async getAuthorizedLocks(): Promise<void> {
		const userName = this.context?.userName;

		logger.info('Getting the Authorized Locks for the user : ' + userName);
		try {
			const authorizeCallResponse = await callSNOWDataService({
				path: '/authorize',
				body: {
					user: userName,
					resource: getBindleLockAuthorizationRequest(getAllBindleLocks()),
					requestId: uuidv4(),
				},
				method: 'POST',
			});
			const authorizedBindleLocks: string[] =
				authorizeCallResponse.authorizedResource.ids;

			this.setState({
				authorizedLocks: authorizedBindleLocks,
				validatedAuth: true,
			}, () => {
				this.buildNavigationItems();
			});
		} catch (e) {
			logger.error(
				'Error while getting the permissions for the user ' + JSON.stringify(e)
			);
			this.setState({
				authorizedLocks: [],
				validatedAuth: true,
			}, () => {
				this.buildNavigationItems();
			});
		}
	}

	render(): React.ReactElement {
		const items = this.state.cachedItems || this.getNavigationItems();

		return (
			<Box>
				<SideNavigation
					header={{ text: 'SNOW', href: '#/' }}
					items={items}
					activeHref={this.props.location.hash || window.location.hash}
				/>
				
				<Box padding={{ horizontal: 's', top: 's' }}>
					<hr style={{ border: 'none', borderTop: '1px solid #d5dbdb', margin: 0 }} />
				</Box>
				<RecentlyVisitedList />
			</Box>
		);
	}
}

export default withRouter(SidePanel);
